import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getPropertyAddresses, getAttachmentsByEntity, createComment, downloadAttachment, downloadThumbnail, API_URL } from '../services/api';
import { formatDate, formatDateTime } from '../utils/dateUtils';
import { ArrowLeft, MapPin, Loader2, FileText, Briefcase, MessageCircle, Paperclip, User, AlertCircle, Send, ExternalLink, ChevronRight, Download, Eye } from 'lucide-react';
import api from '../services/api';

const statusLabels = { 0: 'Draft', 1: 'Assigned', 2: 'Sent', 3: 'Accepted', 4: 'Declined', 5: 'Completed', '-1': 'Cancelled' };
const statusColors = { 0: 'bg-slate-100 text-slate-600', 1: 'bg-yellow-100 text-yellow-700', 2: 'bg-blue-100 text-blue-700', 3: 'bg-green-100 text-green-700', 4: 'bg-red-100 text-red-600', 5: 'bg-emerald-100 text-emerald-700', '-1': 'bg-red-100 text-red-600' };
const jobStatusLabels = { 0: 'Pending', 1: 'Scheduled', 2: 'Ongoing', 3: 'Completed' };
const jobStatusColors = { 0: 'bg-slate-100 text-slate-600', 1: 'bg-blue-100 text-blue-700', 2: 'bg-yellow-100 text-yellow-700', 3: 'bg-green-100 text-green-700' };
const mapsLink = (address) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;

function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [comment, setComment] = useState('');

  const { data: property, isLoading } = useQuery({
    queryKey: ['property', id],
    queryFn: () => api.get(`/api/property-addresses/${id}`).then(r => r.data),
  });

  const { data: attachments } = useQuery({
    queryKey: ['propertyAttachments', id],
    queryFn: () => getAttachmentsByEntity('property', id).then(r => r.data).catch(() => []),
  });

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" size={32} /></div>;
  if (!property) return <div className="text-center py-20 text-slate-500">Property not found</div>;

  const fullAddress = [property.address_line1, property.address_line2, property.address_line3, property.address_line4, property.city, property.region, property.postal_code, property.country].filter(Boolean).join(', ');
  const quotes = property.quotes || [];
  const jobs = property.jobs || [];
  const comments = property.comments || [];

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    try {
      await createComment({ entity_type: 'property', entity_id: id, comment });
      setComment('');
      queryClient.invalidateQueries({ queryKey: ['property', id] });
      toast.success('Comment added');
    } catch { toast.error('Failed to add comment'); }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="property-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="back-btn">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <p className="text-sm text-slate-500">
            <Link to={`/clients/${property.client_id}`} className="text-[#3498db] hover:underline">{property.client_name || 'Client'}</Link>
            <ChevronRight size={14} className="inline mx-1" />
            Property Details
          </p>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">{property.address_line1}</h1>
        </div>
      </div>

      {/* Property Info Card */}
      <div className="bg-white rounded-card border" data-testid="property-info-card">
        <div className="p-6 bg-slate-50/50 border-b">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                <MapPin size={20} className="text-[#f39c12]" />
                {property.address_line1}
              </div>
              <a href={mapsLink(fullAddress)} target="_blank" rel="noopener noreferrer" className="text-sm text-[#3498db] hover:underline mt-1 inline-flex items-center gap-1" data-testid="property-maps-link">
                {fullAddress}
                <ExternalLink size={12} className="opacity-60" />
              </a>
            </div>
            <div className="flex gap-6 text-center">
              <div className="bg-white border rounded-lg px-5 py-3">
                <p className="text-2xl font-bold text-[#3498db]">{quotes.length}</p>
                <p className="text-xs text-slate-500 mt-1">Quotes</p>
              </div>
              <div className="bg-white border rounded-lg px-5 py-3">
                <p className="text-2xl font-bold text-[#18bc9c]">{jobs.length}</p>
                <p className="text-xs text-slate-500 mt-1">Jobs</p>
              </div>
            </div>
          </div>
        </div>
        {/* Property Details */}
        <div className="p-6 grid grid-cols-2 md:grid-cols-3 gap-6 text-sm">
          {property.tenant_first_name && (
            <div>
              <span className="text-slate-500">Tenant</span>
              <p className="font-medium mt-0.5">{property.tenant_first_name} {property.tenant_last_name}</p>
              {property.tenant_phone && <p className="text-slate-500 text-xs">{property.tenant_phone}</p>}
              {property.tenant_email && <p className="text-slate-500 text-xs">{property.tenant_email}</p>}
            </div>
          )}
          {property.owner_first_name && (
            <div>
              <span className="text-slate-500">Owner</span>
              <p className="font-medium mt-0.5">{property.owner_first_name} {property.owner_last_name}</p>
              {property.owner_phone && <p className="text-slate-500 text-xs">{property.owner_phone}</p>}
              {property.owner_email && <p className="text-slate-500 text-xs">{property.owner_email}</p>}
            </div>
          )}
          {property.property_manager_id && (
            <div>
              <span className="text-slate-500">Property Manager</span>
              <p className="font-medium mt-0.5">{property.property_manager_name || 'Assigned'}</p>
            </div>
          )}
        </div>
      </div>

      {/* Quotes Section */}
      <div className="bg-white rounded-card border overflow-hidden" data-testid="property-quotes">
        <div className="p-4 bg-slate-50 border-b flex items-center gap-2">
          <FileText size={18} className="text-[#3498db]" />
          <h2 className="font-semibold text-slate-700">Quotes ({quotes.length})</h2>
        </div>
        {quotes.length === 0 ? (
          <div className="p-8 text-center text-slate-400"><FileText size={32} className="mx-auto mb-2" /><p>No quotes for this property</p></div>
        ) : (
          <table className="w-full" data-testid="property-quotes-table">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Quote #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Job Title</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Total</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Jobs</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {quotes.map(q => (
                <tr key={q.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-sm">{q.quote_number}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">{q.job_titles || '-'}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusColors[q.status] || 'bg-slate-100'}`}>{statusLabels[q.status] || 'Unknown'}</span></td>
                  <td className="px-4 py-3 text-sm font-medium">${q.total_cost?.toFixed(2) || '0.00'}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{q.created_at ? formatDate(q.created_at) : '-'}</td>
                  <td className="px-4 py-3 text-sm">{q.jobs_count > 0 ? <span className="text-green-600 font-medium">{q.jobs_count}</span> : <span className="text-slate-400">0</span>}</td>
                  <td className="px-4 py-3 text-right"><Link to={`/quotes/${q.id}`} className="text-[#3498db] text-xs hover:underline" data-testid={`view-quote-${q.id}`}>View</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Jobs Section */}
      <div className="bg-white rounded-card border overflow-hidden" data-testid="property-jobs">
        <div className="p-4 bg-slate-50 border-b flex items-center gap-2">
          <Briefcase size={18} className="text-[#18bc9c]" />
          <h2 className="font-semibold text-slate-700">Jobs ({jobs.length})</h2>
        </div>
        {jobs.length === 0 ? (
          <div className="p-8 text-center text-slate-400"><Briefcase size={32} className="mx-auto mb-2" /><p>No jobs for this property</p></div>
        ) : (
          <table className="w-full" data-testid="property-jobs-table">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Job #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Quote #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Job Title</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Progress</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Date</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {jobs.map(j => (
                <tr key={j.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-sm">{j.job_number}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{j.quote_number || '-'}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">{j.job_titles || '-'}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-1 text-xs font-semibold rounded-full ${jobStatusColors[j.status] || 'bg-slate-100'}`}>{jobStatusLabels[j.status] || 'Unknown'}</span></td>
                  <td className="px-4 py-3 text-sm">{j.jobs_completed}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{j.created_at ? formatDate(j.created_at) : '-'}</td>
                  <td className="px-4 py-3 text-right"><Link to={`/jobs/${j.id}`} className="text-[#3498db] text-xs hover:underline" data-testid={`view-job-${j.id}`}>View</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Attachments Section */}
      <div className="bg-white rounded-card border overflow-hidden" data-testid="property-attachments">
        <div className="p-4 bg-slate-50 border-b flex items-center gap-2">
          <Paperclip size={18} className="text-[#f39c12]" />
          <h2 className="font-semibold text-slate-700">Attachments ({attachments?.length || 0})</h2>
        </div>
        {(!attachments || attachments.length === 0) ? (
          <div className="p-8 text-center text-slate-400"><Paperclip size={32} className="mx-auto mb-2" /><p>No attachments for this property</p></div>
        ) : (
          <div className="p-4 grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
            {attachments.map((att, idx) => (
              <div key={att.id || idx} className="border rounded-lg overflow-hidden group">
                {att.content_type?.startsWith('image/') ? (
                  <div className="aspect-square bg-slate-100 relative">
                    <img src={`${API_URL}/api/attachments/${att.id}/thumbnail`} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-2 transition-opacity">
                      <a href={`${API_URL}/api/attachments/${att.id}/download`} className="p-1.5 bg-white rounded-full"><Download size={14} /></a>
                    </div>
                  </div>
                ) : (
                  <div className="aspect-square bg-slate-50 flex flex-col items-center justify-center p-2">
                    <FileText size={24} className="text-slate-400" />
                    <p className="text-xs text-slate-500 mt-1 truncate w-full text-center">{att.filename}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Comments Section */}
      <div className="bg-white rounded-card border" data-testid="property-comments">
        <div className="p-4 bg-slate-50 border-b flex items-center gap-2">
          <MessageCircle size={18} className="text-slate-500" />
          <h2 className="font-semibold text-slate-700">Comments</h2>
        </div>
        <div className="p-4">
          {comments.length === 0 ? (
            <div className="text-slate-500 text-sm flex items-center gap-2 py-4 justify-center">
              <AlertCircle size={16} /><span>No comments yet.</span>
            </div>
          ) : (
            <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
              {comments.map((c, idx) => (
                <div key={c.id || `${c.created_at}-${idx}`} className="bg-slate-50 p-3 rounded" data-testid={`property-comment-${c.id || idx}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <User size={14} className="text-slate-400" />
                    <span className="text-sm font-medium text-slate-700">{c.user_name || 'Unknown'}</span>
                    <span className="text-xs text-slate-400">{formatDateTime(c.created_at)}</span>
                  </div>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap">{c.comment}</p>
                </div>
              ))}
            </div>
          )}
          <form onSubmit={handleAddComment} className="flex gap-2" data-testid="property-comment-form">
            <input type="text" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment..." className="flex-1 px-3 py-2 border border-slate-300 rounded text-sm" data-testid="property-comment-input" />
            <button type="submit" className="px-4 py-2 bg-primary text-white rounded text-sm hover:bg-primary-hover flex items-center gap-1.5" data-testid="property-comment-submit">
              <Send size={14} /> Add
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default PropertyDetail;
